import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper';
import { useTheme } from '@emotion/react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListSubheader,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import {
  buildTreeData,
  isNodeDescendant,
} from '../../utils/energyTreeFunctions';

import { getInitiativeInclusionsForNode } from '@/features/energyxplorer/utils/energyTreeNodeInitiativeInclusion';
import { usePutEnergyTreeNodesMutation } from '../../../../api/recalcV2/endpoints/energyTreeNodesApiSlice';
import { validateMoveNode } from '../../utils/energyTreeNodesValidationMiddleware';

// * Redux
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import { useDispatch, useSelector } from 'react-redux';
import InitiativeInclusionNotice from './InitiativeInclusionNotice';
import ValidationNotice from './ValidationNotice';

const MoveNodeDialog = ({
  open,
  onClose,
  clickedNode,
  handleClose,
  tree,
  energyGroup,
}) => {
  const theme = useTheme();
  const account = useSelector(getSelectedAccount);
  const dispatch = useDispatch();
  const [parentId, setParentId] = useState(null);
  const [hoveredPath, setHoveredPath] = useState([]);
  const [putEnergyTreeNodes] = usePutEnergyTreeNodesMutation();

  const inclusions = useMemo(() => {
    const nodesFound = getInitiativeInclusionsForNode(
      clickedNode?.energyTreeNodeId,
      energyGroup,
    );
    return nodesFound;
  }, [clickedNode?.energyTreeNodeId, energyGroup]);

  const virtualInitiativeInclusions = useMemo(
    () =>
      (inclusions || []).filter(
        ({ viaNode }) =>
          viaNode && (!viaNode.dataStreams || viaNode.dataStreams.length === 0),
      ),
    [inclusions],
  );

  // Build options excluding current node and its descendants
  const treeOptions = useMemo(() => {
    const filtered = tree?.filter(
      (n) =>
        n.energyTreeNodeId !== clickedNode?.energyTreeNodeId &&
        !isNodeDescendant(
          n.energyTreeNodeId,
          clickedNode?.energyTreeNodeId,
          tree,
        ),
    );
    return buildTreeData(filtered);
  }, [tree, clickedNode]);

  const selectedParent =
    hoveredPath.length > 0 ? hoveredPath[hoveredPath.length - 1] : null;

  // ✅ New validation (replaces actionValidation)
  const validationResult = useMemo(() => {
    if (!clickedNode || !selectedParent) return { isValid: true, warnings: [] };
    return validateMoveNode({
      currentNode: clickedNode,
      parent: selectedParent,
      tree,
    });
  }, [clickedNode, selectedParent, tree]);

  const handleUpdateNode = async () => {
    if (!clickedNode || !parentId) return;

    const payload = { ...clickedNode, parentId };

    try {
      await putEnergyTreeNodes({
        accountId: account.AccountId,
        energyTreeNodeId: clickedNode.energyTreeNodeId,
        updatedEnergyTreeNode: payload,
      }).unwrap();
      dispatch(showSnackbar({ message: 'Node flyttet', severity: 'success' }));
      handleClose();
    } catch (error) {
      console.error('Failed to move node:', error);
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl ved flytning af node',
          severity: 'error',
          response: error,
        }),
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" fontSize="1.25rem">
          {`Flytning af node`}&nbsp;
          {clickedNode?.name}
          <Box
            sx={{
              backgroundColor: getTypeColor(clickedNode?.type),
              color: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              ml: 1,
              height: 28,
              width: 28,
              justifyContent: 'center',
              fontSize: '14px',
            }}
          >
            <i className={`fa-solid ${getTypeIcon(clickedNode?.type)}`} />
          </Box>
        </Box>

        <i className="fa-solid fa-arrows-turn-to-dots"></i>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2">
          Vælg en ny forælder til noden ved at søge eller vælge fra listen
          nedenfor.
        </Typography>
        <Autocomplete
          id="parent-node-selector"
          fullWidth
          sx={{ mb: 2 }}
          options={treeOptions}
          getOptionLabel={(option) => option.name}
          onChange={(event, newValue) => {
            setParentId(newValue ? newValue.energyTreeNodeId : null);
          }}
          groupBy={() => 'single-group'}
          filterOptions={(options, { inputValue }) => {
            const lowerInput = inputValue.toLowerCase();
            return options.filter((option) =>
              option._path?.some((n) =>
                n.name.toLowerCase().includes(lowerInput),
              ),
            );
          }}
          renderGroup={(params) => (
            <li key={params.key}>
              <ListSubheader sx={{ bgcolor: 'transparent' }}>
                <Chip
                  variant="filled"
                  size="large"
                  sx={{
                    backgroundColor:
                      theme.palette.mode === 'dark' ? '#121212' : '#FFFFFF',
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                  }}
                  label={
                    <Box component="span">
                      {hoveredPath.map((n) => n.name).join(' / ')}
                      {clickedNode?.name && (
                        <Box
                          component="span"
                          sx={{
                            color: validationResult.isValid
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                            fontWeight: 600,
                            ml: 0.5,
                          }}
                        >
                          {validationResult.isValid ? '+ /' : '− /'}{' '}
                          {clickedNode.name}
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListSubheader>
              {params.children}
            </li>
          )}
          renderOption={(props, option) => (
            <li
              {...props}
              key={option.energyTreeNodeId}
              onMouseEnter={() => {
                const path = option._path || [];
                const timeout = setTimeout(() => setHoveredPath(path), 200);
                option._hoverTimeout = timeout;
              }}
              style={{ paddingLeft: `${option._path?.length * 24}px` }}
            >
              <Box
                sx={{
                  backgroundColor: getTypeColor(option?.type),
                  borderRadius: '25px',
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginRight: 1,
                }}
              >
                <i
                  style={{ color: 'white' }}
                  className={`fa-solid ${getTypeIcon(option?.type)}`}
                />
              </Box>
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vælg ny forælder"
              placeholder="Søg efter ny parent"
              variant="filled"
              error={!validationResult.isValid}
            />
          )}
        />

        <InitiativeInclusionNotice
          virtualInitiativeInclusions={virtualInitiativeInclusions}
          mode="move" // or "add"
        />

        {hoveredPath.length > 0 && (
          <ValidationNotice validationResult={validationResult} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuller</Button>
        <Button
          variant="contained"
          onClick={handleUpdateNode}
          disabled={!validationResult.isValid || parentId === null}
        >
          Flyt Node
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveNodeDialog;
